import { getErrorsFromConditions, transformConditionsToBackend } from '@/common/components/ConditionsEditor/helpers'
import { multiValueMatchTypes } from '@/common/components/MatchTypeEditor/constants'
import { isNonBlankValueField } from '@/common/components/MatchTypeEditor/helpers'
import { getPreheaderText } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/Preheader/helpers'
import { findEventConfig } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/Trigger/TriggerEvent/helpers'

export const generateTriggerId = () => Math.random().toString(36).substring(2, 11)

export const areTriggersValid = triggers => {
  return triggers.every(trigger => {
    if (!trigger.type) return false

    const eventConfig = findEventConfig(trigger.type)

    if (eventConfig?.valueOptions && (!trigger.values || trigger.values.length === 0)) return false

    if (eventConfig?.inputOptions?.field) {
      const value = trigger[eventConfig.inputOptions.field]

      if (value === '' || value === null || value === undefined) return false
    }

    return true
  })
}

export const transformSubject = data => {
  const transformedData = {}

  Object.keys(data.subject).forEach(language => {
    transformedData[language] = {
      html: data.subject[language],
      text: data.subject_text[language] || data.subject[language],
    }
  })

  return transformedData
}

export const transformContent = data => {
  const transformedData = {}

  Object.keys(data.content).forEach(language => {
    transformedData[language] = {
      text: '',
      html: data.content[language],
    }
  })

  return transformedData
}

export const transformPreheader = data => data.preheader || {}

export const parseContent = content => {
  const parsedData = {}

  Object.keys(content).forEach(language => {
    parsedData[language] = content[language].html
  })

  return parsedData
}

export const parseSubject = subject => {
  const parsedData = {}

  Object.keys(subject).forEach(language => {
    parsedData[language] = subject[language].html
  })

  return parsedData
}

export const parseTextSubject = subject => {
  const parsedData = {}

  Object.keys(subject).forEach(language => {
    parsedData[language] = subject[language].text || subject[language].html
  })

  return parsedData
}

const parseTextPreheader = preheader => {
  const parsedData = {}

  Object.keys(preheader).forEach(language => {
    parsedData[language] = getPreheaderText(preheader[language])
  })

  return parsedData
}

const getTriggersPayload = triggers => {
  return triggers.map(trigger => {
    const eventConfig = findEventConfig(trigger.type)

    const payload = {
      type: trigger.type,
      values: trigger.values || [],
    }

    if (eventConfig?.inputOptions?.field) {
      payload[eventConfig.inputOptions.field] = trigger[eventConfig.inputOptions.field]
    }

    return payload
  })
}

export const getEditTemplatePayload = ({
  subject,
  preheader,
  mode,
  content,
  liquidContent,
  name,
  description,
  blocks,
  triggers,
  conditions,
  defaultCurrency,
}) => {
  const payload = {
    subject: parseSubject(subject),
    subject_text: parseTextSubject(subject),
    preheader,
    preheader_text: parseTextPreheader(preheader),
    mode,
    content: parseContent(content),
    liquid_content: liquidContent,
  }

  if (name !== undefined) {
    payload.name = name
  }

  if (description !== undefined) {
    payload.description = description
  }

  if (blocks?.length) {
    payload.blocks = blocks
  }

  if (triggers?.length) {
    payload.triggers = getTriggersPayload(triggers)
  }

  if (conditions) {
    payload.conditions = transformConditionsToBackend(conditions, { defaultCurrency })
  }

  return payload
}

const isEmptyHtml = html => {
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent.trim() === '' && div.querySelectorAll('img').length === 0
}

export const hasSubjectInAllAvailableLanguages = (subject, availableLanguages) => {
  return availableLanguages.every(
    language => subject[language.value]?.html && !isEmptyHtml(subject[language.value]?.html)
  )
}

export const hasContentInAllAvailableLanguages = (content, availableLanguages) => {
  return availableLanguages.every(
    language => content[language.value]?.html && !isEmptyHtml(content[language.value]?.html)
  )
}

export const hasLiquidContentInAllAvailableLanguages = (liquidContent, availableLanguages) => {
  return availableLanguages.every(language => liquidContent[language.value]?.trim())
}

export const hasLiquidErrorsInAvailableLanguages = (liquidErrors, availableLanguages) => {
  return availableLanguages.some(language => liquidErrors[language.value])
}

export const getLiveConditionErrors = conditions => {
  return getErrorsFromConditions(conditions).filter(error => {
    const criterion = conditions[error.index].criteria[error.criterionIndex]

    if (!isNonBlankValueField(criterion.field) || !criterion.match_type) return false
    if (multiValueMatchTypes.includes(criterion.match_type)) return criterion.match_values?.length > 0

    return true
  })
}
